import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGlobe, FiMail, FiBarChart, FiEdit, FiLayers, FiZap, FiCalendar } = FiIcons;

const RoleBreakdown = () => {
  const sectors = [
    { title: 'Web Updates', icon: FiGlobe, color: 'text-skyblue', bg: 'bg-skyblue-100', tasks: ['CMS Page Edits', 'Landing Page Build', 'Bug Reporting', 'Homepage Refresh'] },
    { title: 'Content/SEO', icon: FiEdit, color: 'text-sage', bg: 'bg-sage-100', tasks: ['Blog Formatting', 'Keyword Tagging', 'Meta Description Updates', 'Image Optimization'] },
    { title: 'Email Marketing', icon: FiMail, color: 'text-lavender', bg: 'bg-lavender-100', tasks: ['Campaign Setup', 'List Uploads', 'A/B Test Config', 'Final QA Send'] },
    { title: 'Campaign Coord', icon: FiLayers, color: 'text-goldenrod', bg: 'bg-goldenrod-100', tasks: ['Asset Collection', 'Timeline Tracking', 'Stakeholder Follow-up', 'Launch Checklist'] },
    { title: 'Reporting', icon: FiBarChart, color: 'text-crimson', bg: 'bg-crimson-100', tasks: ['Weekly Pulse', 'Monthly Deep Dive', 'Ad-hoc Analysis', 'Data Cleaning'] },
    { title: 'Automations', icon: FiZap, color: 'text-charcoal', bg: 'bg-charcoal-100', tasks: ['Workflow Maintenance', 'Zapier Errors', 'Lead Routing Checks', 'Tag Management'] }
  ];

  const temporalMap = {
    daily: ['Check site uptime/alerts', 'Triage incoming asset requests', 'Process urgent content updates', 'Review social/email calendar'],
    weekly: ['Build & stage email campaigns', 'Update weekly reporting dashboard', 'SEO hygiene check (broken links)', 'Team sync & status update'],
    monthly: ['Full monthly performance report', 'Competitor analysis deep-dive', 'Archive old assets', 'Plan next month content calendar']
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-charcoal mb-4">Coordinator Role Breakdown</h1>
        <p className="text-lg text-charcoal-500">
          A structured map of responsibilities, moving away from "general support" to distinct ownership sectors.
        </p>
      </motion.div>

      {/* Sectors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {sectors.map((sector, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="glass-card p-6 rounded-xl hover:shadow-md transition-shadow border-t-4 border-t-transparent hover:border-t-sage"
          >
            <div className="flex items-center mb-4">
              <div className={`w-10 h-10 ${sector.bg} rounded-lg flex items-center justify-center mr-3`}>
                <SafeIcon icon={sector.icon} className={`w-5 h-5 ${sector.color}`} />
              </div>
              <h3 className="font-bold text-charcoal">{sector.title}</h3>
            </div>
            <ul className="space-y-2">
              {sector.tasks.map((task, tIdx) => (
                <li key={tIdx} className="text-sm text-charcoal-500 flex items-start">
                  <span className="mr-2 text-sage">•</span>
                  {task}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      {/* Temporal Map */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="glass-card rounded-xl p-8 bg-gradient-to-br from-white to-sand-100"
      >
        <h2 className="text-2xl font-bold text-charcoal mb-6 flex items-center">
          <SafeIcon icon={FiCalendar} className="w-6 h-6 text-goldenrod mr-3" />
          Temporal Responsibility Map
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-lg font-bold text-charcoal mb-4 border-b-2 border-sage pb-2">Daily Rhythm</h3>
            <ul className="space-y-3">
              {temporalMap.daily.map((item, i) => (
                <li key={i} className="flex items-start text-sm text-charcoal-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-sage mt-1.5 mr-2 flex-shrink-0"></div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold text-charcoal mb-4 border-b-2 border-skyblue pb-2">Weekly Sprint</h3>
            <ul className="space-y-3">
              {temporalMap.weekly.map((item, i) => (
                <li key={i} className="flex items-start text-sm text-charcoal-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-skyblue mt-1.5 mr-2 flex-shrink-0"></div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold text-charcoal mb-4 border-b-2 border-lavender pb-2">Monthly Cycle</h3>
            <ul className="space-y-3">
              {temporalMap.monthly.map((item, i) => (
                <li key={i} className="flex items-start text-sm text-charcoal-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-lavender mt-1.5 mr-2 flex-shrink-0"></div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RoleBreakdown;